import { clsx } from 'clsx';
import { PropsWithChildren } from 'react';

import {
  formatCount,
  formatPropertyTotalArea,
} from '@monoimin/utils/formatters';
import { RealEstateDict } from '@monoimin/intl';
import { Icons } from '@monoimin/atoms';

import type { IconNamesUnion, PropertyCardDetails } from '@monoimin/sb-types';

export interface PropertyDetailsProps {
  location?: string;
  id?: string | number;
  variant?: 'page' | 'card';
  totalArea: PropertyCardDetails['totalArea'];
  realEstateDict?: RealEstateDict;
  bedroomCount?: any;
  bathroomCount?: any;
  classNames?: {
    container?: string;
    location?: string;
    id?: string;
  };
}

export function PropertyDetails({
  totalArea,
  bedroomCount,
  bathroomCount,
  location,
  id,
  variant,
  classNames,
  realEstateDict,
}: PropertyDetailsProps) {
  const totalAreaString = formatPropertyTotalArea(totalArea);
  const bedroomCountString = formatCount(bedroomCount);
  const bathroomCountString = formatCount(bathroomCount);
  const isPageVariant = variant === 'page';

  return (
    <div
      className={clsx('flex gap-x-6 ', classNames?.container, {
        'md:flex-row flex-col': isPageVariant,
      })}
    >
      <IconItem
        icon='location'
        isPageVariant={isPageVariant}
        className={classNames?.location}
      >
        {location}
      </IconItem>
      <IconItem icon='square' isPageVariant={isPageVariant}>
        {totalAreaString}
      </IconItem>
      {Boolean(bedroomCountString) && (
        <IconItem icon='bed' isPageVariant={isPageVariant}>
          {bedroomCountString}{' '}
          {isPageVariant ? realEstateDict?.['bedrooms'] : ''}
        </IconItem>
      )}
      {Boolean(bathroomCountString) && (
        <IconItem icon='bathroom' isPageVariant={isPageVariant}>
          {bathroomCountString} {isPageVariant ? realEstateDict?.['baths'] : ''}
        </IconItem>
      )}

      <IconItem
        icon='info'
        isPageVariant={isPageVariant}
        className={classNames?.id}
      >
        {id}
      </IconItem>
    </div>
  );
}

const IconItem = ({
  icon,
  className,
  children,
  isPageVariant,
}: PropsWithChildren<{
  icon: IconNamesUnion;
  className?: string;
  isPageVariant?: boolean;
}>) => {
  if (!children) {
    return null;
  }

  const iconClassName = clsx('h-5 w-5 stroke-current');

  return (
    <div
      className={clsx('flex whitespace-nowrap', className, {
        'items-center': isPageVariant,
      })}
    >
      {isPageVariant ? (
        <div className='bg-grey rounded-full h-fit p-4 mr-5'>
          <Icons icon={icon} className={iconClassName} />
        </div>
      ) : (
        <Icons icon={icon} className={iconClassName} />
      )}
      {children}
    </div>
  );
};

export default PropertyDetails;
